/**
 * Custom Cloudflare Image Loader for Next.js
 *
 * Transforms Cloudflare Image Delivery URLs (imagedelivery.net) to use
 * flexible variants with width and quality parameters for optimal srcSet generation.
 *
 * Features:
 * - Bypasses SVG images (Cloudflare cannot optimize SVGs)
 * - Configurable quality via NEXT_PUBLIC_IMAGE_QUALITY env variable
 * - Uses format=auto for automatic WebP/AVIF serving based on browser support
 * - Non-Cloudflare URLs pass through unchanged
 *
 * @see https://developers.cloudflare.com/images/transform-images/transform-via-url/
 */

/**
 * Check if URL is a Cloudflare Image Delivery URL
 * @param {string} src - The image source URL
 * @returns {boolean}
 */
function isCloudflareUrl(src) {
  const cloudflareHost =
    process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGE_HOST || "imagedelivery.net";
  return src.includes(cloudflareHost);
}

/**
 * Check if URL is an SVG image
 * @param {string} src - The image source URL
 * @returns {boolean}
 */
function isSvgImage(src) {
  // Check file extension or content type hints
  const lowerSrc = src.toLowerCase();
  return lowerSrc.includes(".svg") || lowerSrc.includes("image/svg");
}

/**
 * Transform a Cloudflare Image URL to use flexible variants
 *
 * Cloudflare Image URLs follow this pattern:
 * https://imagedelivery.net/{accountHash}/{imageId}/{variant}
 *
 * We replace the variant (e.g., "public") with width/quality parameters:
 * https://imagedelivery.net/{accountHash}/{imageId}/w=640,q=80,format=auto
 *
 * @param {string} src - Original Cloudflare image URL
 * @param {number} width - Desired width in pixels
 * @param {number} quality - Image quality (1-100)
 * @returns {string} - Transformed URL with flexible variant
 */
function transformCloudflareUrl(src, width, quality) {
  try {
    const url = new URL(src);

    // Split pathname into parts: ['', accountHash, imageId, variant]
    const pathParts = url.pathname.split("/");

    // Remove the current variant (last segment like 'public')
    if (pathParts.length >= 4) {
      pathParts.pop();
    }

    // Build the new variant with width, quality, and auto format
    // format=auto serves WebP/AVIF to supported browsers
    const variant = `w=${width},q=${quality},format=auto`;

    // Reconstruct the URL
    return `${url.origin}${pathParts.join("/")}/${variant}`;
  } catch (error) {
    // If URL parsing fails, return original src
    console.warn("Failed to transform Cloudflare URL:", src, error);
    return src;
  }
}

/**
 * Next.js Image Loader function
 *
 * Called by next/image for each image to generate the src URL.
 * For responsive images with sizes prop, this is called multiple times
 * with different width values to build the srcSet.
 *
 * @param {Object} params - Loader parameters from Next.js
 * @param {string} params.src - Original image source URL
 * @param {number} params.width - Requested width for this srcSet entry
 * @param {number} [params.quality] - Optional quality override (1-100)
 * @returns {string} - Final URL to use for this image/srcSet entry
 */
function cloudflareLoader({ src, width, quality }) {
  // Get default quality from env, fallback to 70
  const defaultQuality = parseInt(
    process.env.NEXT_PUBLIC_IMAGE_QUALITY || "65",
    10
  );
  const imageQuality = quality || defaultQuality;

  // Skip SVG images - Cloudflare cannot optimize them
  if (isSvgImage(src)) {
    return src;
  }

  // Transform Cloudflare URLs to use flexible variants
  if (isCloudflareUrl(src)) {
    return transformCloudflareUrl(src, width, imageQuality);
  }

  // For non-Cloudflare URLs, return as-is
  // Next.js will handle them through remotePatterns if configured
  return src;
}

module.exports = cloudflareLoader;
